import React, { useCallback, useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pin, Plus, Trash2, Check, Eye, Megaphone } from "lucide-react";

const inputCls = "w-full px-3 py-2.5 rounded-xl border-2 border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-white text-sm";

/* Shared by both the Principal shell (/principal/notices) and the Teacher
   dashboard — a school's staff (teacher/principal/admin) post here; only a
   principal/admin can pin. Reading a notice marks it read for that staff
   member, driving the unread badge in PrincipalShell's tab nav. */
export default function SchoolNotices({ schoolId }) {
  const { user } = useAuth();
  const [notices, setNotices] = useState(null);
  const [staffCount, setStaffCount] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);

  const canPin = user?.role === "principal" || user?.role === "admin";

  const load = useCallback(async () => {
    if (!schoolId) return;
    try {
      const [{ data }, { data: sc }] = await Promise.all([
        client.get("/school/notices", { params: { school_id: schoolId } }),
        client.get("/school/notices/staff-count", { params: { school_id: schoolId } }),
      ]);
      setNotices(data.notices);
      setStaffCount(sc.staff_count);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await client.post(`/school/notices/${id}/read`);
      setNotices((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true, read_by: [...(n.read_by || []), user.id] } : n)));
    } catch { /* non-critical */ }
  };

  const create = async () => {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      await client.post("/school/notices", { school_id: schoolId, title: title.trim(), body: body.trim(), pinned });
      setTitle(""); setBody(""); setPinned(false); setOpen(false);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await client.delete(`/school/notices/${id}`);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const togglePin = async (n) => {
    try {
      await client.patch(`/school/notices/${n.id}`, { pinned: !n.pinned });
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  if (!schoolId) return null;

  return (
    <div data-testid="school-notices">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="hv-heading text-2xl text-hv-navy">Notice Board</h2>
          <p className="text-hv-slate text-sm mt-1">Notices for everyone on your school&apos;s staff.</p>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="new-notice-btn" className="hv-btn-primary inline-flex items-center gap-2">
          <Plus size={18} strokeWidth={2.5} /> Post a notice
        </button>
      </div>

      {open && (
        <div className="hv-card p-5 mt-4" data-testid="new-notice-form">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
                 data-testid="notice-title" className={inputCls} />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Message"
                    data-testid="notice-body" className={`${inputCls} mt-3`} />
          {canPin && (
            <label className="mt-3 inline-flex items-center gap-2 text-sm text-hv-navy font-bold">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="notice-pin-checkbox" />
              Pin to top
            </label>
          )}
          <div className="mt-3 flex gap-2">
            <button onClick={create} disabled={busy} data-testid="notice-submit" className="hv-btn-primary">
              {busy ? "Posting…" : "Post notice"}
            </button>
            <button onClick={() => setOpen(false)} className="hv-btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {err && <p className="text-hv-coral text-sm mt-3 font-bold">{err}</p>}

      <div className="mt-5 space-y-3">
        {(notices || []).map((n) => (
          <div key={n.id} data-testid={`notice-${n.id}`}
               className={`hv-card p-5 relative ${n.pinned ? "border-2 border-hv-coral/40" : ""}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                {n.pinned && <Pin size={14} className="text-hv-coral shrink-0" strokeWidth={2.5} />}
                <h4 className="hv-heading text-hv-navy text-lg truncate">{n.title}</h4>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {canPin && (
                  <button onClick={() => togglePin(n)} data-testid={`notice-pin-${n.id}`}
                          className="text-hv-slate hover:text-hv-coral" title={n.pinned ? "Unpin" : "Pin"}>
                    <Pin size={16} strokeWidth={2.5} />
                  </button>
                )}
                {(canPin || n.created_by_id === user?.id) && (
                  <button onClick={() => remove(n.id)} data-testid={`notice-delete-${n.id}`}
                          className="text-hv-slate hover:text-hv-coral">
                    <Trash2 size={16} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            </div>
            <div className="text-hv-slate text-xs mt-1">
              {n.created_by_name}{n.created_by_role ? ` · ${n.created_by_role[0].toUpperCase()}${n.created_by_role.slice(1)}` : ""} · {(n.created_at || "").slice(0, 16).replace("T", " ")}
            </div>
            <p className="text-hv-navy text-sm mt-3 whitespace-pre-wrap">{n.body}</p>
            <div className="mt-3 flex items-center justify-between text-xs text-hv-slate">
              <span className="inline-flex items-center gap-1.5">
                <Eye size={13} strokeWidth={2.5} />
                Seen by {(n.read_by || []).length}{staffCount ? ` of ${staffCount}` : ""}
              </span>
              {!n.is_read ? (
                <button onClick={() => markRead(n.id)} data-testid={`notice-mark-read-${n.id}`}
                        className="hv-btn-secondary py-1 px-2.5 text-xs inline-flex items-center gap-1">
                  <Check size={13} strokeWidth={2.5} /> Mark read
                </button>
              ) : (
                <span className="text-hv-teal font-bold">Read</span>
              )}
            </div>
          </div>
        ))}
        {notices && !notices.length && (
          <div className="hv-card p-7 text-hv-slate inline-flex items-center gap-2">
            <Megaphone size={18} /> No notices yet.
          </div>
        )}
        {!notices && <div className="p-6 text-hv-slate">Loading…</div>}
      </div>
    </div>
  );
}
